/** Local intake helpers — mock/dev until backend OCR + bulk import APIs exist. */

export const BULK_IMPORT_TEMPLATE_COLUMNS = [
  'first_name', 'last_name', 'email', 'phone', 'business_legal_name', 'ein', 'ssn', 'dob',
  'personal_address', 'business_address', 'employer', 'personal_income', 'business_income',
  'business_start_date', 'desired_funding_amount', 'target_product', 'experian_score',
  'transunion_score', 'equifax_score', 'current_highest_limit', 'monthly_revenue',
  'monthly_deposits', 'bank_name', 'time_in_business', 'time_at_address', 'status', 'tags',
  'source', 'notes',
];

export const BULK_IMPORT_MOCK_ROWS = [
  { row: 1, status: 'Ready', first_name: 'Marie', last_name: 'Samuelle', email: 'marie@example.com', phone: '+1 407 555 0198', business_legal_name: 'MS Beauty Studio LLC', ssn: '123456789', ein: '884821931', desired_funding_amount: '50000' },
  { row: 2, status: 'Ready', first_name: 'Jean', last_name: 'Samuelle', email: 'jean@example.com', phone: '+1 407 555 0199', business_legal_name: 'MS Beauty Studio LLC', ssn: '987654421', ein: '884821931', desired_funding_amount: '30000' },
  { row: 3, status: 'Duplicate', first_name: 'Jhon', last_name: 'Smith', email: 'contact@credfortune.com', phone: '9084705074', business_legal_name: 'FU dev LLC', ssn: '111228888', ein: '121111111', desired_funding_amount: '25000', error: 'Existing client match' },
  { row: 4, status: 'Error', first_name: 'Missing', last_name: 'Contact', email: '', phone: '', business_legal_name: 'No Contact LLC', ssn: '000000000', ein: '112222222', desired_funding_amount: '15000', error: 'Missing phone or email' },
  { row: 5, status: 'Ready', first_name: 'Sarah', last_name: 'Pierre', email: 'sarah@example.com', phone: '+1 954 555 0102', business_legal_name: 'SP Consulting LLC', ssn: '555667722', ein: '223333333', desired_funding_amount: '75000' },
];

export function maskSsnLast4(ssn) {
  const digits = String(ssn || '').replace(/\D/g, '');
  const last4 = digits.slice(-4) || '0000';
  return { masked: `***-**-${last4}`, last4, full: digits.length >= 9 ? `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}` : '' };
}

export function maskEin(ein) {
  const digits = String(ein || '').replace(/\D/g, '');
  if (digits.length < 9) return { masked: ein || '', full: ein || '' };
  return { masked: `**-***${digits.slice(-4)}`, full: `${digits.slice(0, 2)}-${digits.slice(2, 9)}`, last4: digits.slice(-4) };
}

export const REVIEW_FIELD_CATEGORY_ORDER = [
  'Identity',
  'Address',
  'Employment',
  'Income',
  'Business',
  'Tax',
  'Financial',
  'Sensitive',
];

const FULL_SSN_PATTERN = /\b\d{3}-\d{2}-\d{4}\b/;
const FULL_EIN_PATTERN = /\b\d{2}-\d{7}\b/;

export function maskSensitiveExtractedValue(label, value) {
  const text = String(value || '');
  if (!text) return text;
  if (label === 'SSN' || label === 'SSN (Last 4)') {
    if (FULL_SSN_PATTERN.test(text)) {
      const last4 = text.replace(/\D/g, '').slice(-4);
      return `***-**-${last4}`;
    }
    return text;
  }
  if (label === 'EIN' || label === 'EIN (Last 4)') {
    if (FULL_EIN_PATTERN.test(text)) {
      const last4 = text.replace(/\D/g, '').slice(-4);
      return `**-***${last4}`;
    }
    return text;
  }
  return text;
}

export function isNoisyExtractedAddress(value) {
  const text = String(value || '').trim();
  if (!text) return false;
  if (text.length > 80) return true;
  if (/ORGAN DONOR|Sex\.|ves BRO|NONE 05\//i.test(text)) return true;
  if ((text.match(/\d{1,2}\/\d{1,2}\/\d{2,4}/g) || []).length >= 2) return true;
  return false;
}

const INVALID_NAME_FRAGMENTS = [
  'social security number',
  'your social security',
  'address',
  'and ssn',
  'business suite',
  'no business name',
  'tax return',
  'department of treasury',
  'internal revenue service',
  'profit or loss',
  'lastname',
  'type or print',
];

export function isNoisyExtractedName(value) {
  const text = String(value || '').trim();
  if (!text) return false;
  if (text.length > 60) return true;
  const lower = text.toLowerCase();
  if (INVALID_NAME_FRAGMENTS.some((fragment) => lower.includes(fragment))) return true;
  if (/\b(?:form|schedule|omb)\b/i.test(lower)) return true;
  if (/\b\d{3}-\d{2}-\d{4}\b/.test(text)) return true;
  if ((text.match(/\d/g) || []).length > 2) return true;
  if (/\b(?:street|st|ave|avenue|road|suite|apt|blvd|parkway)\b/i.test(lower)) return true;
  return false;
}

export const DOCUMENT_TYPE_USED_LABELS = {
  driver_license: 'Driver license / ID',
  tax_return_1040: 'Form 1040',
  schedule_c: 'Schedule C',
  tax_return_1120: 'Form 1120',
  form_1120_s: 'Form 1120-S',
  form_1065: 'Form 1065',
  w2: 'W-2',
  '1099_nec': '1099-NEC',
  '1099_k': '1099-K',
  '1099_misc': '1099-MISC',
  schedule_e: 'Schedule E',
  schedule_se: 'Schedule SE',
  schedule_k1: 'Schedule K-1',
  payroll_941: 'Form 941',
  payroll_940: 'Form 940',
  client_profile_screenshot: 'Client profile screenshot',
};

export function formatDocumentTypeUsedLabel(documentType) {
  if (!documentType) return null;
  return DOCUMENT_TYPE_USED_LABELS[documentType] || String(documentType).replace(/_/g, ' ');
}

export function groupReviewFieldsByCategory(fields = []) {
  const grouped = REVIEW_FIELD_CATEGORY_ORDER.reduce((acc, category) => {
    acc[category] = [];
    return acc;
  }, {});

  fields.forEach((row) => {
    const category = row.category || 'Identity';
    if (!grouped[category]) grouped[category] = [];
    grouped[category].push(row);
  });

  return REVIEW_FIELD_CATEGORY_ORDER
    .filter((category) => grouped[category]?.length)
    .map((category) => ({ category, fields: grouped[category] }));
}

export function buildContactFullName({ firstName, middleName, lastName } = {}) {
  return [firstName, middleName, lastName]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
}

export function emptyManualIntakeForm() {
  return {
    firstName: '', middleName: '', lastName: '', email: '', phone: '', status: 'Draft', source: 'Manual intake',
    ownership: '', ssn: '', dob: '', personalAddress: '', timeLiveInHouse: '', housingPayment: '',
    businessLegalName: '', ein: '', businessStartDate: '', businessAddress: '', businessPhone: '', businessEmail: '',
    industry: '', entityType: '',
    personalIncome: '', businessIncome: '', employer: '', timeWorkingAt: '', monthlyRevenue: '', monthlyDeposits: '',
    businessExpenses: '', savings: '', retirement: '', bankName: '',
    experian: '', transUnion: '', equifax: '', highestLimit: '', inquiries: '', utilization: '', derogatoryItems: '', readiness: '',
    desiredFunding: '', targetProduct: '', fundingStage: '', applicationType: '',
    personalFundingAmount: '', zeroPercentAmount: '', businessLocAmount: '', targetLender: '',
    applicationEmailCreated: '', applicationPasswordCreated: '', hasExistingApplicationPassword: false,
    applicationPasswordMasked: '', hasApplicationPassword: false,
    ssnMasked: '', hasClientSsn: false,
    einMasked: '', hasClientEin: false,
    tags: '', advisorOwner: '', notes: '',
    draftId: '',
  };
}

const MASKED_SECRET_PATTERN = /^[•*]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AGENCY_EMAIL_PATTERN = /^(contact|support|info|hello|admin|team)@/i;

function isEmailAddress(value) {
  return EMAIL_PATTERN.test(String(value || '').trim());
}

function logMappingGuardBlocked() {
  if (process.env.NODE_ENV !== 'production') {
    console.info('[FundGenius Mapping Guard] blocked suspicious field mapping');
  }
}

function resolveClientRecordSources(client = {}) {
  return [
    client,
    client?.intake,
    client?.identityProfile,
    client?.identity_profile,
  ].filter((source) => source && typeof source === 'object');
}

function toSnakeCase(value = '') {
  return String(value).replace(/([A-Z])/g, '_$1').toLowerCase();
}

export function getClientOnlyField(client = {}, possibleKeys = []) {
  const sources = resolveClientRecordSources(client);
  for (const key of possibleKeys) {
    const variants = [key, toSnakeCase(key)];
    for (const variant of variants) {
      for (const source of sources) {
        const value = source?.[variant];
        if (value != null && String(value).trim() !== '') {
          return value;
        }
      }
    }
  }
  return '';
}

export function sanitizeEditablePassword(value) {
  if (value == null || value === '') return '';
  if (isMaskedSensitiveValue(value)) {
    logMappingGuardBlocked();
    return '';
  }
  const text = String(value).trim();
  if (!text) return '';
  if (text.includes('•') || text.includes('***') || /^\*+$/.test(text)) {
    logMappingGuardBlocked();
    return '';
  }
  return text;
}

export function sanitizePersonalAddress(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (isEmailAddress(text) || (AGENCY_EMAIL_PATTERN.test(text) && isEmailAddress(text))) {
    logMappingGuardBlocked();
    return '';
  }
  return text;
}

export function isMaskedSensitiveValue(value) {
  if (value == null || value === '') return true;
  const text = String(value).trim();
  if (!text) return true;
  if (text === '••••••••' || text === '********') return true;
  if (MASKED_SECRET_PATTERN.test(text)) return true;
  if (text.includes('***') || text.includes('••')) return true;
  if (/^\*{2,}-\*{2,}-/.test(text)) return true;
  if (/^\*{2,}-\*{3}/.test(text)) return true;
  return false;
}

function pickClientField(client, intake, ...keys) {
  for (const key of keys) {
    const fromIntake = intake?.[key];
    if (fromIntake != null && String(fromIntake).trim() !== '') return fromIntake;
    const fromClient = client?.[key];
    if (fromClient != null && String(fromClient).trim() !== '') return fromClient;
  }
  return getClientOnlyField(client, keys);
}

function splitTagsFromNotes(notes = '', tags = '') {
  if (String(tags || '').trim()) {
    return { notes: notes || '', tags: String(tags).trim() };
  }
  const raw = String(notes || '');
  const match = raw.match(/^([\s\S]*?)\n?Tags:\s*(.+)$/);
  if (match) {
    return { notes: match[1].trim(), tags: match[2].trim() };
  }
  return { notes: raw.trim(), tags: '' };
}

function buildClientSsnMasked(client = {}) {
  const masked = getClientOnlyField(client, ['ssnMasked', 'ssn_masked']) || client.ssnMasked;
  const maskedText = String(masked || '').trim();
  if (maskedText && (maskedText.includes('*') || maskedText.includes('•'))) {
    return maskedText;
  }
  const last4 = getClientOnlyField(client, ['ssnLast4', 'ssn_last4']) || client.ssnLast4;
  return last4 ? `***-**-${last4}` : '';
}

function buildClientEinMasked(client = {}) {
  const masked = getClientOnlyField(client, ['einMasked', 'ein_masked']) || client.einMasked;
  const maskedText = String(masked || '').trim();
  if (maskedText && (maskedText.includes('*') || maskedText.includes('•'))) {
    return maskedText;
  }
  const last4 = getClientOnlyField(client, ['einLast4', 'ein_last4']) || client.einLast4;
  return last4 ? `**-***${last4}` : '';
}

export function clientToManualIntakeForm(client = {}) {
  const base = emptyManualIntakeForm();
  if (!client || typeof client !== 'object') return base;

  const intake = client.intake || {};
  const clientEmail = pickClientField(client, intake, 'email');
  const personalAddress = sanitizePersonalAddress(
    getClientOnlyField(client, ['personalAddress', 'personal_address']),
  );

  const { notes, tags } = splitTagsFromNotes(
    pickClientField(client, intake, 'notes'),
    pickClientField(client, intake, 'tags'),
  );

  const ssnMasked = buildClientSsnMasked(client);
  const einMasked = buildClientEinMasked(client);
  const hasClientSsn = Boolean(client.hasClientSsn || client.ssnLast4 || ssnMasked);
  const hasClientEin = Boolean(client.hasClientEin || client.einLast4 || einMasked);
  const hasApplicationPassword = Boolean(client.hasApplicationPasswordCreated || client.hasApplicationPassword);
  const applicationPasswordMasked = hasApplicationPassword
    ? (client.applicationPasswordCreatedMasked || '••••••••')
    : '';

  return {
    ...base,
    firstName: pickClientField(client, intake, 'firstName'),
    middleName: pickClientField(client, intake, 'middleName'),
    lastName: pickClientField(client, intake, 'lastName'),
    email: clientEmail,
    phone: pickClientField(client, intake, 'phone'),
    status: pickClientField(client, intake, 'status') || client.status || base.status,
    source: pickClientField(client, intake, 'source') || client.sourceLabel || client.source || base.source,
    ownership: pickClientField(client, intake, 'ownership', 'ownershipPercentage'),
    ssn: '',
    ssnMasked,
    hasClientSsn,
    dob: pickClientField(client, intake, 'dob'),
    personalAddress,
    timeLiveInHouse: pickClientField(client, intake, 'timeLiveInHouse'),
    housingPayment: pickClientField(client, intake, 'housingPayment'),
    businessLegalName: pickClientField(client, intake, 'businessLegalName', 'businessName', 'legalBusinessName'),
    ein: '',
    einMasked,
    hasClientEin,
    businessStartDate: pickClientField(client, intake, 'businessStartDate'),
    businessAddress: pickClientField(client, intake, 'businessAddress'),
    businessPhone: pickClientField(client, intake, 'businessPhone'),
    businessEmail: pickClientField(client, intake, 'businessEmail'),
    industry: pickClientField(client, intake, 'industry'),
    entityType: pickClientField(client, intake, 'entityType'),
    personalIncome: pickClientField(client, intake, 'personalIncome'),
    businessIncome: pickClientField(client, intake, 'businessIncome'),
    employer: pickClientField(client, intake, 'employer'),
    timeWorkingAt: pickClientField(client, intake, 'timeWorkingAt'),
    monthlyRevenue: pickClientField(client, intake, 'monthlyRevenue') || (client.monthlyRevenue ?? ''),
    monthlyDeposits: pickClientField(client, intake, 'monthlyDeposits'),
    businessExpenses: pickClientField(client, intake, 'businessExpenses'),
    savings: pickClientField(client, intake, 'savings'),
    retirement: pickClientField(client, intake, 'retirement', 'retirementAccount'),
    bankName: pickClientField(client, intake, 'bankName'),
    experian: pickClientField(client, intake, 'experian', 'experianScore') || (client.creditScore ?? ''),
    transUnion: pickClientField(client, intake, 'transUnion', 'transunionScore'),
    equifax: pickClientField(client, intake, 'equifax', 'equifaxScore'),
    highestLimit: pickClientField(client, intake, 'highestLimit', 'currentHighestLimit'),
    inquiries: pickClientField(client, intake, 'inquiries', 'openInquiries'),
    utilization: pickClientField(client, intake, 'utilization', 'utilizationPercent'),
    derogatoryItems: pickClientField(client, intake, 'derogatoryItems'),
    readiness: pickClientField(client, intake, 'readiness', 'readinessPercent')
      || (client.readinessScore != null ? `${client.readinessScore}` : ''),
    desiredFunding: pickClientField(client, intake, 'desiredFunding') || (client.requestedAmount ?? ''),
    targetProduct: pickClientField(client, intake, 'targetProduct'),
    fundingStage: pickClientField(client, intake, 'fundingStage'),
    applicationType: pickClientField(client, intake, 'applicationType'),
    personalFundingAmount: pickClientField(client, intake, 'personalFundingAmount'),
    zeroPercentAmount: pickClientField(client, intake, 'zeroPercentAmount', 'zeroPercentCreditCardAmount'),
    businessLocAmount: pickClientField(client, intake, 'businessLocAmount'),
    targetLender: pickClientField(client, intake, 'targetLender'),
    applicationEmailCreated: pickClientField(client, intake, 'applicationEmailCreated'),
    applicationPasswordCreated: '',
    applicationPasswordMasked,
    hasExistingApplicationPassword: hasApplicationPassword,
    hasApplicationPassword,
    tags,
    advisorOwner: pickClientField(client, intake, 'advisorOwner'),
    notes,
  };
}

export function parseBusinessStartDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    if (date.getFullYear() === Number(year) && date.getMonth() === Number(month) - 1 && date.getDate() === Number(day)) {
      return `${year}-${month}-${day}`;
    }
    return null;
  }

  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    if (date.getFullYear() === Number(year) && date.getMonth() === Number(month) - 1 && date.getDate() === Number(day)) {
      return `${year}-${mm}-${dd}`;
    }
    return null;
  }

  return null;
}

export function parseReadinessPercent(value) {
  const n = parseInt(String(value || '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) ? n : null;
}

export function clientFromManualIntake(form, { agencyId } = {}) {
  const name = buildContactFullName(form) || 'New Client';
  const ssnInfo = maskSsnLast4(form.ssn);
  const id = `fcl_local_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const readinessScore = parseReadinessPercent(form.readiness);

  return {
    id,
    agencyId: agencyId || 'agency_internal_001',
    name,
    firstName: form.firstName,
    middleName: form.middleName,
    lastName: form.lastName,
    businessName: form.businessLegalName?.trim() || null,
    legalBusinessName: form.businessLegalName?.trim() || null,
    email: form.email,
    phone: form.phone,
    status: form.status || 'Documents Pending',
    source: form.source,
    readinessScore: readinessScore ?? 0,
    readinessTier: readinessScore >= 80 ? 'Good' : readinessScore >= 60 ? 'Fair' : 'Needs Work',
    documentStatus: 'Partial',
    requestedAmount: parseMoney(form.desiredFunding),
    creditScore: parseInt(form.experian, 10) || null,
    monthlyRevenue: parseMoney(form.monthlyRevenue),
    intake: { ...form, ssnLast4: ssnInfo.last4, ssnMasked: ssnInfo.masked },
    _secure: { ssnFull: ssnInfo.full, einFull: maskEin(form.ein).full },
    updatedAt: new Date().toISOString(),
  };
}

export function clientFromExtractedFields(fields, { agencyId } = {}) {
  return clientFromManualIntake({
    ...emptyManualIntakeForm(),
    firstName: fields.firstName || '',
    lastName: fields.lastName || '',
    ownerName: [fields.firstName, fields.lastName].filter(Boolean).join(' '),
    ssn: fields.ssnFull || fields.ssn || '',
    ein: fields.ein || '',
    businessLegalName: fields.businessLegalName || '',
    email: fields.email || '',
    phone: fields.phone || '',
    source: 'Document upload',
  }, { agencyId });
}

export function clientFromBulkRow(row, { agencyId } = {}) {
  return clientFromManualIntake({
    ...emptyManualIntakeForm(),
    firstName: row.first_name || row.firstName || '',
    lastName: row.last_name || row.lastName || '',
    email: row.email || '',
    phone: row.phone || '',
    businessLegalName: row.business_legal_name || row.businessLegalName || '',
    ssn: row.ssn || '',
    ein: row.ein || '',
    desiredFunding: row.desired_funding_amount || row.desiredFunding || '',
    experian: row.experian_score || '',
    transUnion: row.transunion_score || '',
    equifax: row.equifax_score || '',
    status: row.status || 'Documents Pending',
    tags: row.tags || '',
    source: row.source || 'Bulk import',
    notes: row.notes || '',
  }, { agencyId });
}

export function parseMoney(value) {
  const n = Number(String(value || '').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** Deterministic mock OCR fixtures for unit tests only — not used in production scan flows. */
export function mockExtractFromDocuments(fileNames = []) {
  const hint = (fileNames[0] || '').toLowerCase();
  if (hint.includes('john') || hint.includes('jhon')) {
    return {
      firstName: 'Jhon', lastName: 'Smith', ssnFull: '123-45-8888', ssnMasked: '***-**-8888',
      ein: '12-1111111', businessLegalName: 'FU dev LLC', email: '', phone: '',
    };
  }
  return {
    firstName: 'Marie', lastName: 'Samuelle', ssnFull: '123-45-6789', ssnMasked: '***-**-6789',
    ein: '88-4821931', businessLegalName: 'MS Beauty Studio LLC', email: 'marie@example.com', phone: '+1 407 555 0198',
    personalIncome: '$92,000',
    businessIncome: '$186,000',
  };
}

function sourceLabel(doc) {
  if (doc.customName?.trim()) return doc.customName.trim();
  return doc.fileName;
}

/** @deprecated Test fixtures only — production uses documentExtractionService. */
export function mockExtractFromUploadedDocuments(documents = []) {
  const extractionDocs = documents.filter((doc) => doc.useForExtraction);
  const fileNames = extractionDocs.map((doc) => doc.fileName);
  const clientData = mockExtractFromDocuments(fileNames);
  const fields = [];
  const seen = new Set();

  const pushField = (field, extracted, source, confidence) => {
    const key = `${field}:${extracted}`;
    if (seen.has(key)) return;
    seen.add(key);
    fields.push({
      id: `field_${fields.length + 1}`,
      field,
      current: '—',
      extracted,
      source,
      confidence,
      apply: true,
    });
  };

  extractionDocs.forEach((doc) => {
    const source = sourceLabel(doc);
    const types = doc.documentTypes || [];
    const name = doc.fileName.toLowerCase();

    if (types.includes('Tax return') || name.includes('tax')) {
      pushField('Personal Income', '$92,000', source, 86);
      pushField('Business Income', '$186,000', source, 84);
    }
    if (types.includes('EIN letter') || name.includes('ein')) {
      pushField('EIN', '**-***1931', source, 94);
      pushField('Business Legal Name', clientData.businessLegalName, source, 94);
    }
    if (types.includes('Driver license / ID') || name.includes('license') || name.includes('id')) {
      pushField('First Name', clientData.firstName, source, 91);
      pushField('Last Name', clientData.lastName, source, 91);
      pushField('Personal Address', '211 Palm Ridge Dr, Orlando, FL', source, 78);
    }
    if (types.includes('SSN card') || name.includes('ssn')) {
      pushField('SSN', clientData.ssnMasked, source, 89);
    }
    if (types.includes('Bank statement') || name.includes('bank')) {
      pushField('Monthly Deposits', '$12,000', source, 82);
      pushField('Bank Name', 'Chase', source, 80);
    }
    if (types.includes('Paystub') || name.includes('pay')) {
      pushField('Employer', 'Self-employed', source, 77);
      pushField('Personal Income', '$92,000', source, 83);
    }
    if (types.includes('Proof of address') || types.includes('Utility bill')) {
      pushField('Personal Address', '211 Palm Ridge Dr, Orlando, FL', source, 75);
    }
    if (types.includes('Business registration') || types.includes('Articles of organization')) {
      pushField('Business Legal Name', clientData.businessLegalName, source, 88);
      pushField('Business Start Date', '2021-03-13', source, 72);
    }
    if (types.includes('Other document') && doc.customName?.trim()) {
      pushField('Notes', doc.customName.trim(), source, 65);
    }
  });

  if (!fields.length) {
    pushField('First Name', clientData.firstName, sourceLabel(extractionDocs[0] || { fileName: 'upload' }), 70);
    pushField('Last Name', clientData.lastName, sourceLabel(extractionDocs[0] || { fileName: 'upload' }), 70);
    pushField('Business Legal Name', clientData.businessLegalName, sourceLabel(extractionDocs[0] || { fileName: 'upload' }), 70);
  }

  return { fields, clientData };
}

export function clientDataFromReviewFields(fields, baseClientData = {}) {
  const applied = fields.filter((row) => row.apply);
  const next = { ...baseClientData };
  applied.forEach((row) => {
    switch (row.field) {
      case 'First Name':
        next.firstName = row.extracted;
        break;
      case 'Last Name':
        next.lastName = row.extracted;
        break;
      case 'Owner Name':
        next.ownerName = row.extracted;
        break;
      case 'DOB':
      case 'Date of Birth':
        next.dob = row.extracted;
        break;
      case 'Phone':
      case 'Cell phone':
        next.phone = row.extracted;
        break;
      case 'Email':
        next.email = row.extracted;
        break;
      case 'Occupation':
        next.occupation = row.extracted;
        break;
      case 'Work Phone':
        next.workPhone = row.extracted;
        break;
      case 'City':
        if (!next.personalCity) next.personalCity = row.extracted;
        break;
      case 'State':
        if (!next.personalState) next.personalState = row.extracted;
        break;
      case 'ZIP':
        if (!next.personalZip) next.personalZip = row.extracted;
        break;
      case 'Employer Address':
        next.employerAddress = row.extracted;
        break;
      case 'Employer City':
        next.employerCity = row.extracted;
        break;
      case 'Employer State':
        next.employerState = row.extracted;
        break;
      case 'Employer ZIP':
        next.employerZip = row.extracted;
        break;
      case 'Business Name':
        next.businessLegalName = row.extracted;
        break;
      case 'Business Address':
        next.businessAddress = row.extracted;
        break;
      case 'SSN':
        next.ssnMasked = row.extracted;
        next.ssnFull = row.extracted;
        break;
      case 'EIN':
        next.ein = row.extracted;
        break;
      case 'Business Legal Name':
        next.businessLegalName = row.extracted;
        break;
      case 'Personal Income':
        next.personalIncome = row.extracted;
        break;
      case 'Business Income':
        next.businessIncome = row.extracted;
        break;
      case 'Personal Address':
        next.personalAddress = row.extracted;
        break;
      case 'Date of Birth':
        next.dob = row.extracted;
        break;
      case 'Employer':
        next.employer = row.extracted;
        break;
      case 'Bank Name':
        next.bankName = row.extracted;
        break;
      case 'Monthly Deposits':
        next.monthlyDeposits = row.extracted;
        break;
      case 'Business Start Date':
        next.businessStartDate = row.extracted;
        break;
      default:
        break;
    }
  });
  return next;
}

export function downloadBulkImportTemplate() {
  const header = BULK_IMPORT_TEMPLATE_COLUMNS.join(',');
  const sample = 'Marie,Samuelle,marie@example.com,+14075550198,MS Beauty Studio LLC,884821931,123456789,1988-11-05,211 Palm Ridge Dr Orlando FL,44 Market Plaza Orlando FL,Self-employed,92000,186000,2021-03-13,50000,0% Business Credit Card,720,718,731,25000,15500,12000,Chase,3,4,Documents Pending,book buyer,Manual,Primary owner';
  const blob = new Blob([`${header}\n${sample}\n`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'fundgenius-client-import-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function validateBulkRows(rows, existingClients = []) {
  const existingEmails = new Set(existingClients.map((c) => (c.email || '').toLowerCase()).filter(Boolean));
  const existingPhones = new Set(existingClients.map((c) => String(c.phone || '').replace(/\D/g, '')).filter(Boolean));

  return rows.map((row, index) => {
    const email = (row.email || '').toLowerCase();
    const phone = String(row.phone || '').replace(/\D/g, '');
    const hasContact = Boolean(email || phone);
    const name = [row.first_name || row.firstName, row.last_name || row.lastName].filter(Boolean).join(' ');

    if (!hasContact) {
      return { ...row, row: index + 1, status: 'Error', clientName: name || '—', error: 'Missing phone or email' };
    }
    if (email && existingEmails.has(email)) {
      return { ...row, row: index + 1, status: 'Duplicate', clientName: name, error: 'Existing client match' };
    }
    if (phone && existingPhones.has(phone)) {
      return { ...row, row: index + 1, status: 'Duplicate', clientName: name, error: 'Existing client match' };
    }
    const ssnInfo = maskSsnLast4(row.ssn);
    const einInfo = maskEin(row.ein);
    return {
      ...row,
      row: index + 1,
      status: 'Ready',
      clientName: name,
      ssnMasked: ssnInfo.masked,
      einMasked: einInfo.masked,
      error: '',
    };
  });
}
